export const GfsUnit = {
  None: "",
  Minute: "minute",
  Hour: "hour",
  Piece: "piece",
  LumpSum: "lump_sum",
  Measure: "measure",
  Percent: "percent",
  Gram: "gram",
  Milligram: "milligram",
  Kilogram: "kilogram",
  Liter: "liter",
  Milliliter: "milliliter",
  Centiliter: "centiliter",
  Deciliter: "deciliter",
  Meter: "meter",
  Centimeter: "centimeter",
  SquareMeter: "square_meter",
} as const;

export type GfsStandardUnit = (typeof GfsUnit)[keyof typeof GfsUnit] & string;

export type GfsUnitValue = GfsStandardUnit | (string & {});

type GfsUnitOption = {
  value: GfsUnitValue;
  label: string;
};

const legacyUnitAliases: Record<string, GfsStandardUnit> = {
  minute: GfsUnit.Minute,
  minuten: GfsUnit.Minute,
  min: GfsUnit.Minute,
  hour: GfsUnit.Hour,
  hours: GfsUnit.Hour,
  stunde: GfsUnit.Hour,
  stunden: GfsUnit.Hour,
  h: GfsUnit.Hour,
  piece: GfsUnit.Piece,
  pieces: GfsUnit.Piece,
  stück: GfsUnit.Piece,
  stuck: GfsUnit.Piece,
  pauschale: GfsUnit.LumpSum,
  lump_sum: GfsUnit.LumpSum,
  "lump sum": GfsUnit.LumpSum,
  maßnahme: GfsUnit.Measure,
  massnahme: GfsUnit.Measure,
  measure: GfsUnit.Measure,
  percent: GfsUnit.Percent,
  percentage: GfsUnit.Percent,
  prozent: GfsUnit.Percent,
  gram: GfsUnit.Gram,
  gramm: GfsUnit.Gram,
  milligram: GfsUnit.Milligram,
  milligramm: GfsUnit.Milligram,
  kilogram: GfsUnit.Kilogram,
  kilogramm: GfsUnit.Kilogram,
  liter: GfsUnit.Liter,
  litre: GfsUnit.Liter,
  l: GfsUnit.Liter,
  milliliter: GfsUnit.Milliliter,
  millilitre: GfsUnit.Milliliter,
  ml: GfsUnit.Milliliter,
  centiliter: GfsUnit.Centiliter,
  centilitre: GfsUnit.Centiliter,
  cl: GfsUnit.Centiliter,
  deciliter: GfsUnit.Deciliter,
  decilitre: GfsUnit.Deciliter,
  dl: GfsUnit.Deciliter,
  meter: GfsUnit.Meter,
  metre: GfsUnit.Meter,
  m: GfsUnit.Meter,
  centimeter: GfsUnit.Centimeter,
  centimetre: GfsUnit.Centimeter,
  cm: GfsUnit.Centimeter,
  square_meter: GfsUnit.SquareMeter,
  "square meter": GfsUnit.SquareMeter,
  "m²": GfsUnit.SquareMeter,
};

const unitDisplayNames: Record<GfsStandardUnit, string> = {
  [GfsUnit.None]: "",
  [GfsUnit.Minute]: "Minute",
  [GfsUnit.Hour]: "Stunde",
  [GfsUnit.Piece]: "Stück",
  [GfsUnit.LumpSum]: "Pauschale",
  [GfsUnit.Measure]: "Maßnahme",
  [GfsUnit.Percent]: "Prozent",
  [GfsUnit.Gram]: "Gramm",
  [GfsUnit.Milligram]: "Milligramm",
  [GfsUnit.Kilogram]: "Kilogramm",
  [GfsUnit.Liter]: "Liter",
  [GfsUnit.Milliliter]: "Milliliter",
  [GfsUnit.Centiliter]: "Zentiliter",
  [GfsUnit.Deciliter]: "Deziliter",
  [GfsUnit.Meter]: "Meter",
  [GfsUnit.Centimeter]: "Zentimeter",
  [GfsUnit.SquareMeter]: "Quadratmeter",
};

export const GfsPriceUnitOptions: GfsUnitOption[] = [
  { value: GfsUnit.Piece, label: "Stück" },
  { value: GfsUnit.LumpSum, label: "Pauschale" },
  { value: GfsUnit.Gram, label: "Gramm" },
  { value: GfsUnit.Milligram, label: "Milligramm" },
  { value: GfsUnit.Measure, label: "Maßnahme" },
  { value: GfsUnit.Percent, label: "Prozent" },
  { value: GfsUnit.Kilogram, label: "Kilogramm" },
  { value: GfsUnit.Liter, label: "Liter" },
  { value: GfsUnit.Milliliter, label: "Milliliter" },
  { value: GfsUnit.Centiliter, label: "Zentiliter" },
  { value: GfsUnit.Deciliter, label: "Deziliter" },
];

export const GfsTimePriceUnitOptions: GfsUnitOption[] = [
  { value: GfsUnit.Minute, label: "Minute" },
  { value: GfsUnit.Hour, label: "Stunde" },
];

export const GfsCatalogUnitOptions: GfsUnitOption[] = [
  { value: GfsUnit.Piece, label: "Stück" },
  { value: GfsUnit.Liter, label: "Liter (l)" },
  { value: GfsUnit.Milliliter, label: "Milliliter (ml)" },
  { value: GfsUnit.Kilogram, label: "Kilogramm (kg)" },
  { value: GfsUnit.Gram, label: "Gramm (g)" },
  { value: GfsUnit.Milligram, label: "Milligramm (mg)" },
  { value: GfsUnit.Meter, label: "Meter (m)" },
  { value: GfsUnit.Centimeter, label: "Zentimeter (cm)" },
  { value: GfsUnit.SquareMeter, label: "Quadratmeter (m²)" },
];

export function normalizeGfsUnit(value: string): GfsUnitValue {
  const trimmed = value.trim();
  return legacyUnitAliases[trimmed.toLocaleLowerCase("de-DE")] ?? trimmed;
}

export function formatGfsUnit(value: string): string {
  const normalized = normalizeGfsUnit(value);
  return unitDisplayNames[normalized as GfsStandardUnit] ?? normalized;
}
